import { apiClient } from '@dukene/shared';

// ============================================
// TYPES
// ============================================

export interface AffiliateProfile {
  id: string;
  userId: string;
  displayName: string;
  bio?: string;
  website?: string;
  socialMedia?: {
    twitter?: string;
    facebook?: string;
    instagram?: string;
    youtube?: string;
    linkedin?: string;
    tiktok?: string;
  };
  paymentEmail?: string;
  paymentPhone?: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'SUSPENDED' | 'INACTIVE';
  totalClicks: number;
  totalSales: number;
  totalEarnings: number;
  createdAt: string;
  approvedAt?: string;
  rejectionReason?: string;
  user?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
}

export interface AffiliateLink {
  id: string;
  code: string;
  trackingUrl: string;
  productUrl: string;
  customAlias?: string;
  productId: string;
  productTitle: string;
  productPrice: number;
  productImage?: string;
  commissionType?: 'PERCENTAGE' | 'FIXED';
  commissionValue?: number;
  isActive: boolean;
  totalClicks: number;
  totalSales: number;
  revenue: number;
  totalCommissions: number;
  createdAt: string;
  expiresAt?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
}

export interface Commission {
  id: string;
  commissionAmount: number;
  commissionType: 'PERCENTAGE' | 'FIXED';
  commissionRate: number;
  saleAmount: number;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'PAID';
  createdAt: string;
  approvedAt?: string;
  paidAt?: string;
  link: {
    code: string;
    product: {
      id: string;
      title: string;
      handle: string;
    };
  };
  order: {
    orderNumber: string;
    total: number;
    status: string;
  };
}

export interface AvailableProduct {
  id: string;
  title: string;
  description?: string;
  handle: string;
  price: number;
  compareAtPrice?: number;
  commissionType?: 'PERCENTAGE' | 'FIXED';
  commissionValue?: number;
  images: { url: string }[];
  store: {
    id: string;
    name: string;
    subdomain: string;
    logo?: string;
  };
  categories: {
    category: {
      id: string;
      name: string;
    };
  }[];
}

export interface Category {
  id: string;
  name: string;
  slug: string;
  store?: {
    name: string;
  };
}

export interface DashboardStats {
  overview: {
    totalClicks: number;
    totalSales: number;
    totalEarnings: number;
    conversionRate: number;
  };
  earnings: {
    pending: number;
    approved: number;
    paid: number;
  };
  topPerformingLinks: {
    linkId: string;
    code: string;
    productTitle: string;
    clicks: number;
    sales: number;
    revenue: number;
    commissions: number;
  }[];
  recentActivity: {
    type: 'click' | 'sale' | 'commission' | 'payout';
    description: string;
    amount?: number;
    date: Date;
  }[];
  performanceOverTime: {
    date: string;
    clicks: number;
    sales: number;
    earnings: number;
  }[];
}

export interface PaginatedResponse<T> {
  items: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// ============================================
// API FUNCTIONS
// ============================================

export const affiliateApi = {
  // Profile Management
  async getProfile(): Promise<AffiliateProfile> {
    return await apiClient.get<AffiliateProfile>('/affiliate/profile');
  },

  async registerAffiliate(data: {
    displayName: string;
    bio?: string;
    website?: string;
    socialMedia?: Record<string, string>;
    paymentEmail?: string;
    bankAccount?: string;
    bankName?: string;
    accountHolderName?: string;
    taxId?: string;
    taxCountry?: string;
  }): Promise<AffiliateProfile> {
    return await apiClient.post<AffiliateProfile>('/affiliate/register', data);
  },

  async updateProfile(data: {
    displayName?: string;
    bio?: string;
    website?: string;
    socialMedia?: Record<string, string>;
    paymentEmail?: string;
    paymentPhone?: string;
    bankAccount?: string;
    bankName?: string;
    accountHolderName?: string;
    taxId?: string;
    taxCountry?: string;
  }): Promise<AffiliateProfile> {
    return await apiClient.patch<AffiliateProfile>('/affiliate/profile', data);
  },

  // Products
  async getAvailableProducts(params?: {
    storeId?: string;
    categoryId?: string;
    search?: string;
    commissionType?: 'PERCENTAGE' | 'FIXED';
    minPrice?: number;
    maxPrice?: number;
    isHighCommission?: boolean;
    page?: number;
    limit?: number;
    sortBy?: 'createdAt' | 'price' | 'title' | 'commissionValue';
    sortOrder?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<AvailableProduct>> {
    return await apiClient.get<PaginatedResponse<AvailableProduct>>('/affiliate/products', {
      params,
    });
  },

  async getAvailableCategories(storeId?: string): Promise<Category[]> {
    const response = await apiClient.get<{ success: boolean; data: Category[] }>('/affiliate/products/categories', {
      params: storeId ? { storeId } : undefined
    });
    return response.data;
  },

  // Links Management
  async createLink(data: {
    productId: string;
    customAlias?: string;
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
    expiresAt?: string;
  }): Promise<AffiliateLink> {
    return await apiClient.post<AffiliateLink>('/affiliate/links', data);
  },

  async getLinks(params?: {
    page?: number;
    limit?: number;
    isActive?: boolean;
    productId?: string;
    sortBy?: 'createdAt' | 'totalClicks' | 'totalSales' | 'revenue';
    sortOrder?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<AffiliateLink>> {
    return await apiClient.get<PaginatedResponse<AffiliateLink>>('/affiliate/links', { params });
  },

  async updateLink(
    linkId: string,
    data: {
      customAlias?: string;
      utmSource?: string;
      utmMedium?: string;
      utmCampaign?: string;
      isActive?: boolean;
      expiresAt?: string;
    }
  ): Promise<AffiliateLink> {
    return await apiClient.patch<AffiliateLink>(`/affiliate/links/${linkId}`, data);
  },

  async deleteLink(linkId: string): Promise<void> {
    await apiClient.delete(`/affiliate/links/${linkId}`);
  },

  // Commissions
  async getCommissions(params?: {
    page?: number;
    limit?: number;
    status?: 'PENDING' | 'APPROVED' | 'REJECTED' | 'PAID';
    startDate?: string;
    endDate?: string;
    sortBy?: 'createdAt' | 'commissionAmount';
    sortOrder?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<Commission>> {
    return await apiClient.get<PaginatedResponse<Commission>>('/affiliate/commissions', { params });
  },

  // Dashboard
  async getDashboardStats(): Promise<DashboardStats> {
    return await apiClient.get<DashboardStats>('/affiliate/dashboard/stats');
  },

  // Payouts
  async initiatePayout(data: { amount: number; destination: string; mpesaMethod: string }): Promise<{ token: string }> {
    return await apiClient.post('/affiliate/payouts/initiate', data);
  },

  async requestPayout(data: { token: string; code: string; destination: string; mpesaMethod: string }): Promise<any> {
    return await apiClient.post('/affiliate/payouts', data);
  },
};
